import { Injectable } from '@angular/core';
import { Refinery, Role, UserRole } from 'src/app/core/models';

@Injectable({ providedIn: 'root' })
export class UserPermissionService {
  public get currentUser(): any {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch {
      return null;
    }
  }

  public isReadOnly(user: any = this.currentUser): boolean {
    const roleName = user?.roleName?.toString().trim().toLowerCase();
    const compactRoleName = roleName?.replace(/\s+/g, '');
    return (
      (roleName?.includes('read') && roleName?.includes('only')) ||
      compactRoleName === 'readonly' ||
      user?.permissions?.includes('read only')
    );
  }

  public isSuperAdmin(user: any = this.currentUser): boolean {
    return this.isAdmin(user) && user?.isAdminAtAllSites === true;
  }

  public isAdminAtAllSites(
    sites: Pick<Refinery, 'refineryId'>[],
    roles: Pick<Role, 'roleId' | 'refineryId' | 'roleName' | 'active'>[],
    assignments: Pick<UserRole, 'roleId' | 'active'>[]
  ): boolean {
    if (!sites?.length || !roles?.length || !assignments?.length) {
      return false;
    }

    const isActive = (active: unknown): boolean => Number(active) === 1 || String(active).toLowerCase() === 'true';
    const activeRoleIds = new Set(
      assignments.filter(assignment => isActive(assignment.active)).map(assignment => Number(assignment.roleId))
    );
    // Use the role's site, not the user record's default refinery on assignment rows.
    const adminSiteIds = new Set(
      roles
        .filter(
          role =>
            isActive(role.active) &&
            role.roleName?.trim().toLowerCase() === 'admin' &&
            activeRoleIds.has(Number(role.roleId))
        )
        .map(role => Number(role.refineryId))
    );

    return sites.every(site => {
      const refineryId = Number(site.refineryId);
      return Number.isInteger(refineryId) && refineryId > 0 && adminSiteIds.has(refineryId);
    });
  }

  public isAdmin(user: any = this.currentUser): boolean {
    const roleName = user?.roleName?.toString().trim().toLowerCase();
    return roleName === 'admin' && !this.isReadOnly(user);
  }

  public isMutationPermission(permission: string): boolean {
    return /^(add|create|update|edit|delete|remove|activate|deactivate|upload|assign|save)(\s|$)/.test(
      permission?.trim().toLowerCase() || ''
    );
  }

  public hasPermission(permission: string, user: any = this.currentUser): boolean {
    if (!user) {
      return false;
    }

    const normalizedPermission = permission?.trim().toLowerCase();
    const roleName = user.roleName?.toString().trim().toLowerCase();
    if (normalizedPermission === 'view welder qualification' && (roleName === 'creator' || this.isReadOnly(user))) {
      return true;
    }
    if (this.isReadOnly(user)) {
      if (normalizedPermission === 'system admin' || this.isMutationPermission(normalizedPermission)) {
        return false;
      }
      return user.permissions?.includes(permission) || false;
    }

    if (user.roleName?.toString().trim().toLowerCase() === 'creator') {
      return normalizedPermission !== 'system admin' && (user.permissions?.includes(permission) || false);
    }

    if (normalizedPermission === 'system admin' && this.isAdmin(user)) {
      return true;
    }

    return user.permissions?.includes('system admin') || user.permissions?.includes(permission);
  }
}
